import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Divider
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

// Mock data
import {
  mockOrganizationsList,
  mockExpiringDocumentsSummary,
  mockExpiringByType,
  mockExpiringDocumentsList,
  getExpiryStatusColor,
  getExpiryStatusLabel,
  filterExpiringDocuments,
  generateExpiringDocumentsCsv,
  simulateDownload,
  simulateSendEmailReport,
  simulateDelay
} from '../../../mockData/orgDocIndex';

// MockBadge inline component
const MockBadge = () => (
  <Chip
    label="MOCK"
    size="small"
    color="warning"
    sx={{ ml: 1, fontSize: '10px', height: '20px' }}
  />
);

/**
 * Summary Tiles Component
 */
const SummaryTiles = ({ summary }) => {
  const tiles = [
    {
      label: 'Total Documents',
      value: summary.total,
      color: '#1976d2',
      icon: <ScheduleIcon />
    },
    {
      label: 'Expired',
      value: summary.expired,
      color: '#f44336',
      icon: <ErrorIcon />
    },
    {
      label: 'Expires < 7 Days',
      value: summary.expiresIn0To7Days,
      color: '#ff5722',
      icon: <WarningIcon />
    },
    {
      label: 'Expires 8-30 Days',
      value: summary.expiresIn8To30Days,
      color: '#ff9800',
      icon: <WarningIcon />
    },
    {
      label: 'Expires 31-60 Days',
      value: summary.expiresIn31To60Days,
      color: '#ffc107',
      icon: <ScheduleIcon />
    },
    {
      label: 'Valid',
      value: summary.valid,
      color: '#4caf50',
      icon: <CheckCircleIcon />
    }
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {tiles.map((tile, index) => (
        <Grid item xs={6} sm={4} md={2} key={index}>
          <Card
            sx={{
              bgcolor: tile.color,
              color: 'white',
              height: '100%'
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ mb: 1 }}>{tile.icon}</Box>
              <Typography variant="h4" fontWeight="bold">
                {tile.value}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {tile.label}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

/**
 * Expiry by Type Chart (Simple Bar)
 */
const ExpiryByTypeChart = ({ data }) => {
  const maxTotal = Math.max(...data.map(d => d.total));

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Expiry Status by Document Type
        </Typography>
        <Box sx={{ mt: 2 }}>
          {data.map((item, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" fontWeight="medium">
                  {item.typeName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.total} total
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', height: 24, borderRadius: 1, overflow: 'hidden', bgcolor: '#e0e0e0' }}>
                {/* Expired */}
                <Tooltip title={`Expired: ${item.expired}`}>
                  <Box
                    sx={{
                      width: `${(item.expired / item.total) * 100}%`,
                      bgcolor: '#f44336',
                      transition: 'width 0.3s'
                    }}
                  />
                </Tooltip>
                {/* Expires in 30 days */}
                <Tooltip title={`Expires in 30 days: ${item.expiresIn30Days}`}>
                  <Box
                    sx={{
                      width: `${(item.expiresIn30Days / item.total) * 100}%`,
                      bgcolor: '#ff9800',
                      transition: 'width 0.3s'
                    }}
                  />
                </Tooltip>
                {/* Expires in 60 days */}
                <Tooltip title={`Expires in 60 days: ${item.expiresIn60Days}`}>
                  <Box
                    sx={{
                      width: `${(item.expiresIn60Days / item.total) * 100}%`,
                      bgcolor: '#ffc107',
                      transition: 'width 0.3s'
                    }}
                  />
                </Tooltip>
                {/* Valid */}
                <Tooltip title={`Valid: ${item.valid}`}>
                  <Box
                    sx={{
                      width: `${(item.valid / item.total) * 100}%`,
                      bgcolor: '#4caf50',
                      transition: 'width 0.3s'
                    }}
                  />
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: '#f44336', borderRadius: 0.5 }} />
            <Typography variant="caption">Expired</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: '#ff9800', borderRadius: 0.5 }} />
            <Typography variant="caption">30 days</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: '#ffc107', borderRadius: 0.5 }} />
            <Typography variant="caption">60 days</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: '#4caf50', borderRadius: 0.5 }} />
            <Typography variant="caption">Valid</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

/**
 * Expiring Documents Report Page
 */
const ExpiringDocumentsReport = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [byType, setByType] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [emailing, setEmailing] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    organizationId: 'ALL',
    status: 'ALL',
    documentType: 'ALL',
    organization: '',
    sortBy: 'daysToExpiry'
  });

  // Organization options for dropdown
  const organizationOptions = [
    { value: 'ALL', label: 'All Organizations' },
    ...mockOrganizationsList.map(org => ({ value: org.id, label: `${org.name} (${org.code})` }))
  ];

  // Load data
  const loadData = async () => {
    setLoading(true);
    try {
      await simulateDelay(500);
      setSummary(mockExpiringDocumentsSummary);
      setByType(mockExpiringByType);
      setDocuments(mockExpiringDocumentsList);
      setFilteredDocuments(mockExpiringDocumentsList);
    } catch (error) {
      console.error('Error loading report data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Apply filters
  useEffect(() => {
    const filtered = filterExpiringDocuments(documents, filters);
    setFilteredDocuments(filtered);
    setPage(0);
  }, [filters, documents]);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    await simulateDelay(1000);
    const csv = generateExpiringDocumentsCsv(filteredDocuments);
    simulateDownload(csv, 'expiring_documents_report.csv');
    setExporting(false);
    alert('[MOCK] CSV file downloaded: expiring_documents_report.csv');
  };

  // Handle email
  const handleEmail = async () => {
    setEmailing(true);
    await simulateDelay(1000);
    simulateSendEmailReport('Expiring Documents', ['admin@thaklis.ae']);
    setEmailing(false);
    alert('[MOCK] Report sent to admin@thaklis.ae');
  };

  // Handle filter change
  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
  };

  // Status options
  const statusOptions = [
    { value: 'ALL', label: 'All Statuses' },
    { value: 'EXPIRED', label: 'Expired' },
    { value: 'EXPIRES_URGENTLY', label: 'Expires < 7 days' },
    { value: 'EXPIRES_SOON', label: 'Expires 8-30 days' },
    { value: 'EXPIRES_IN_60', label: 'Expires 31-60 days' },
    { value: 'EXPIRES_IN_90', label: 'Expires 61-90 days' }
  ];

  // Document type options
  const typeOptions = [
    { value: 'ALL', label: 'All Types' },
    ...byType.map(t => ({ value: t.typeCode, label: t.typeName }))
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Expiring Documents Report
          </Typography>
          <MockBadge />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={emailing ? <CircularProgress size={16} /> : <EmailIcon />}
            onClick={handleEmail}
            disabled={emailing}
          >
            Email Report
          </Button>
        </Box>
      </Box>

      {/* Summary Tiles */}
      <SummaryTiles summary={summary} />

      {/* Chart */}
      <ExpiryByTypeChart data={byType} />

      {/* Documents Table */}
      <Card>
        <CardContent>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              select
              label="Organization"
              value={filters.organizationId}
              onChange={handleFilterChange('organizationId')}
              size="small"
              sx={{ minWidth: 250 }}
            >
              {organizationOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              value={filters.status}
              onChange={handleFilterChange('status')}
              size="small"
              sx={{ minWidth: 180 }}
            >
              {statusOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Document Type"
              value={filters.documentType}
              onChange={handleFilterChange('documentType')}
              size="small"
              sx={{ minWidth: 200 }}
            >
              {typeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Search"
              value={filters.organization}
              onChange={handleFilterChange('organization')}
              size="small"
              sx={{ minWidth: 150 }}
              placeholder="Search..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Organization</TableCell>
                  <TableCell>Document Type</TableCell>
                  <TableCell>Document Number</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell align="center">Days</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Authority</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDocuments
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((doc) => (
                    <TableRow key={doc.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {doc.organisation.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {doc.organisation.code}
                        </Typography>
                      </TableCell>
                      <TableCell>{doc.documentType.name}</TableCell>
                      <TableCell>{doc.documentNumber || '-'}</TableCell>
                      <TableCell>{doc.expiryDate}</TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ color: getExpiryStatusColor(doc.status) }}
                        >
                          {doc.daysToExpiry}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getExpiryStatusLabel(doc.status)}
                          size="small"
                          sx={{
                            bgcolor: getExpiryStatusColor(doc.status),
                            color: 'white',
                            fontSize: '0.7rem'
                          }}
                        />
                      </TableCell>
                      <TableCell>{doc.issuingAuthority}</TableCell>
                    </TableRow>
                  ))}
                {filteredDocuments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No documents match the selected filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredDocuments.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default ExpiringDocumentsReport;
